// src/router/index.js
import { createRouter, createWebHistory } from "vue-router";
import LandingPage from "@/views/LandingPage.vue";
import ContactUsPage from "@/views/ContactUsPage.vue";
import SignUpPage from "@/views/auth/SignUpPage.vue";
import LogInPage from "@/views/auth/LogInPage.vue";
import VerifyEmailPage from "@/views/auth/VerifyEmailPage.vue";
import VerifyPhonePage from "@/views/auth/VerifyPhonePage.vue";
import LinkEmailPage from "@/views/auth/LinkEmailPage.vue";
import ProjectsPage from "@/views/project/ProjectsPage.vue";
import ProjectPage from "@/views/project/ProjectPage.vue";
import NewEmail from "@/views/communication/NewEmailPage.vue";
import TextPage from "@/views/communication/TextPage.vue";
import NewTextMessage from "@/views/communication/NewTextPage.vue";
import { useAuthStore } from "@/stores/auth";
import EditProject from "@/views/project/EditProjectPage.vue";
import ContactBookPage from "@/views/contact/ContactBookPage.vue";
import ContactViewPage from "@/views/contact/ContactViewPage.vue";
import ContactEditPage from "@/views/contact/ContactEditPage.vue";
import ProjectAlarmsPage from "@/views/project/ProjectAlarmsPage.vue";
import CreateAlarmPage from "@/views/project/CreateAlarmPage.vue";
import EmailViewPage from "@/views/communication/EmailPage.vue";
import UserAccount from "@/views/account/UserAccountPage.vue";
import AccountEditPage from "@/views/account/AccountEditPage.vue";
import CreateContactPage from "@/views/contact/CreateContactPage.vue";
import MergeContactPage from "@/views/contact/MergeContactPage.vue";
import AddContactToProject from "@/views/project/AddContactToProjectPage.vue";
import AssignProjectToContactPage from "@/views/contact/AssignProjectToContactPage.vue";
import NewCallPage from "@/views/communication/NewCallPage.vue";
import CallLogPage from "@/views/communication/CallLogPage.vue";
import EmailSuccessPage from "@/views/auth/LinkEmailSuccessPage.vue";
import InstructionsPage from "@/views/instruction/InstructionsPage.vue";
import CreateInstructionsPage from "@/views/instruction/CreateInstructionsPage.vue";
import ViewInstructionPage from "@/views/instruction/ViewInstructionPage.vue";
import EditInstructionPage from "@/views/instruction/EditInstructionPage.vue";
import AgentsPage from "@/views/agent/AgentsPage.vue";
import CreateAgentPage from "@/views/agent/CreateAgentPage.vue";
import EditAgentPage from "@/views/agent/EditAgentPage.vue";
import AgentViewPage from "@/views/agent/AgentViewPage.vue";
import ActivationsPage from "@/views/activation/ActivationsPage.vue";
import CreateActivationPage from "@/views/activation/CreateActivationPage.vue";
import EditActivationPage from "@/views/activation/EditActivationPage.vue";
import ViewActivationPage from "@/views/activation/ViewActivationPage.vue";
import CreateProjectPage from "@/views/project/CreateProjectPage.vue";
import CalendarViewPage from "@/views/communication/CalendarViewPage.vue";
import SystemLogPage from "@/views/communication/SystemLogPage.vue";

const routes = [
  // Public Routes
  {
    path: "/",
    name: "LandingPage",
    component: LandingPage,
  },
  {
    path: "/contact",
    name: "Contact",
    component: ContactUsPage,
  },
  {
    path: "/signup",
    name: "SignUp",
    redirect: { name: "LogIn" },
  },
  {
    path: "/login",
    name: "LogIn",
    component: LogInPage,
  },
  {
    path: "/verify-email",
    name: "VerifyEmail",
    component: VerifyEmailPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/verify-phone",
    name: "VerifyPhone",
    component: VerifyPhonePage,
    meta: { requiresAuth: true },
  },
  {
    path: "/link-email",
    name: "LinkEmail",
    component: LinkEmailPage,
    meta: { requiresAuth: true },
  },

  {
    path: "/gmail-success",
    name: "GmailSuccessPage",
    component: EmailSuccessPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/outlook-success",
    name: "OutlookSuccessPage",
    component: EmailSuccessPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/link-email-success",
    name: "LinkEmailSuccessPage",
    component: EmailSuccessPage,
    meta: { requiresAuth: true },
  },
  // Protected Routes
  {
    path: "/projects",
    name: "ProjectsPage",
    component: ProjectsPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/projects/create",
    name: "CreateProjectPage",
    component: CreateProjectPage,
    meta: { requiresAuth: true },
  },

  {
    path: "/project/:id",
    name: "ProjectPage",
    component: ProjectPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/project/:id/edit",
    name: "EditProject",
    component: EditProject,
    meta: { requiresAuth: true },
  },
  {
    path: "/merge-contact",
    name: "MergeContactPage",
    component: MergeContactPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/project/:id/add-contact",
    name: "AddContactToProject",
    component: AddContactToProject,
    meta: { requiresAuth: true },
  },
  {
    path: "/project/:id/assign-project",
    name: "AssignProjectToContact",
    component: AssignProjectToContactPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/project/:id/new-email",
    name: "NewEmail",
    component: NewEmail,
    meta: { requiresAuth: true, requiresEmailAuth: true },
  },
  {
    path: "/new-email",
    name: "NewEmailStandalone",
    component: NewEmail,
    meta: { requiresAuth: true, requiresEmailAuth: true },
  },
  {
    path: "/project/:id/email",
    name: "EmailViewPage",
    component: EmailViewPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/project/:id/calendar",
    name: "CalendarViewPage",
    component: CalendarViewPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/project/:id/system",
    name: "SystemLogPage",
    component: SystemLogPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/project/:projectId/text/:threadId?",
    name: "TextPage",
    component: TextPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/project/:id/phone-log",
    name: "CallLogPage",
    component: CallLogPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/project/:id/new-text",
    name: "NewTextMessage",
    component: NewTextMessage,
    meta: { requiresAuth: true },
  },
  {
    path: "/project/new-text",
    name: "NewTextMessage",
    component: NewTextMessage,
    meta: { requiresAuth: true },
  },
  {
    path: "/project/:id/alarms",
    name: "ProjectAlarmsPage",
    component: ProjectAlarmsPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/project/:id/create-alarm",
    name: "CreateAlarmPage",
    component: CreateAlarmPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/contacts",
    name: "ContactBookPage",
    component: ContactBookPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/contacts/create",
    name: "CreateContactPage",
    component: CreateContactPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/contact/:id",
    name: "ContactViewPage",
    component: ContactViewPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/contact/:id/edit",
    name: "ContactEditPage",
    component: ContactEditPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/my-account",
    name: "UserAccount",
    component: UserAccount,
    meta: { requiresAuth: true },
  },
  {
    path: "/edit-account",
    name: "AccountEditPage",
    component: AccountEditPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/new-call",
    name: "NewCall",
    component: NewCallPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/instructions",
    name: "InstructionsPage",
    component: InstructionsPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/create-instructions",
    name: "CreateInstructionsPage",
    component: CreateInstructionsPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/instructions/:id",
    name: "ViewInstructionPage",
    component: ViewInstructionPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/instructions/:id/edit",
    name: "EditInstructionPage",
    component: EditInstructionPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/agents",
    name: "AgentsPage",
    component: AgentsPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/create-agent",
    name: "CreateAgentPage",
    component: CreateAgentPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/agent/:id",
    name: "AgentViewPage",
    component: AgentViewPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/agent/:id/edit",
    name: "EditAgentPage",
    component: EditAgentPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/activations",
    name: "ActivationsPage",
    component: ActivationsPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/create-activation",
    name: "CreateActivationPage",
    component: CreateActivationPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/activation/:id",
    name: "ViewActivationPage",
    component: ViewActivationPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/activation/:id/edit",
    name: "EditActivationPage",
    component: EditActivationPage,
    meta: { requiresAuth: true },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach((to, from, next) => {
  const authStore = useAuthStore();
  authStore.initializeAuthState(); // Initialize the auth state on every route change

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    // User is not authenticated, redirect to login page
    next({ name: "LogIn" });
  } else if (
    ["LogIn", "SignUp", "LandingPage"].includes(to.name) &&
    authStore.isAuthenticated
  ) {
    // Authenticated user trying to access auth pages, redirect to projects page
    next({ name: "ProjectsPage" });
  } else if (to.meta.requiresEmailAuth && !authStore.isEmailAuthorized) {
    // User has not authorized Gmail, redirect to LinkEmail page
    next({ name: "LinkEmail" });
  } else {
    // Proceed to the route
    next();
  }
});

export default router;
